import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';
import { Product } from '@/types/fulfill/inventory';

import type { AutocompleteInputChangeReason } from '@mui/material/Autocomplete';
import type { SyntheticEvent } from 'react';

import { CreateKitFormData, createKitSchema } from './schema';

interface UseCreateKitProps {
  onClose: () => void;
  onProductsAdded?: () => Promise<void>;
}

const MIN_CATALOG_SEARCH_LENGTH = 3;
const CATALOG_SEARCH_DEBOUNCE_MS = 400;

const useCreateKit = (props: UseCreateKitProps) => {
  const { onClose, onProductsAdded } = props;
  const [currentStep, setCurrentStep] = useState(1);
  const [isCreating, setIsCreating] = useState(false);

  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogProducts, setCatalogProducts] = useState<Product[]>([]);
  const [isLoadingCatalog, setIsLoadingCatalog] = useState(false);
  const [selectedProductsMap, setSelectedProductsMap] = useState<Map<string, Product>>(new Map());

  const http = initializeHttpProvider();

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateKitFormData>({
    resolver: yupResolver(createKitSchema),
    mode: 'onBlur',
    defaultValues: { components: [] },
  });

  const { fields, replace } = useFieldArray({ control, name: 'components' });
  const catalogDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleChangeStep = useCallback((step: number) => {
    setCurrentStep(step);
  }, []);

  const searchCatalogProducts = useCallback(
    async (search: string) => {
      setIsLoadingCatalog(true);
      try {
        const response = await http.inventory.products({
          onlyActive: true,
          page_size: 20,
          ...(search.length >= MIN_CATALOG_SEARCH_LENGTH && { search }),
        });
        setCatalogProducts(response.products.filter((product) => !product.is_kit));
      } catch {
        toast.error('No se pudieron cargar los productos del catálogo.');
      } finally {
        setIsLoadingCatalog(false);
      }
    },
    [http],
  );

  useEffect(() => {
    return () => {
      if (catalogDebounceRef.current) {
        clearTimeout(catalogDebounceRef.current);
      }
    };
  }, []);

  const onOpenCatalog = useCallback(() => {
    if (catalogProducts.length === 0 && !isLoadingCatalog) {
      void searchCatalogProducts(catalogSearch);
    }
  }, [catalogProducts.length, catalogSearch, isLoadingCatalog, searchCatalogProducts]);

  const onChangeCatalogInput = useCallback(
    (_event: SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => {
      if (reason === 'reset') return;

      setCatalogSearch(value);

      if (catalogDebounceRef.current) {
        clearTimeout(catalogDebounceRef.current);
      }

      if (value.length === 0) {
        void searchCatalogProducts('');
        return;
      }

      if (value.length >= MIN_CATALOG_SEARCH_LENGTH) {
        catalogDebounceRef.current = setTimeout(() => {
          void searchCatalogProducts(value);
        }, CATALOG_SEARCH_DEBOUNCE_MS);
      }
    },
    [searchCatalogProducts],
  );

  const selectedProducts = fields.reduce<Product[]>((accumulator, field) => {
    const product = selectedProductsMap.get(field.sku);
    if (product) accumulator.push(product);
    return accumulator;
  }, []);

  const onChangeSelectedProducts = useCallback(
    (_event: SyntheticEvent, newValue: Product[]) => {
      const newComponents = newValue.map((product) => {
        const existingField = fields.find((field) => field.sku === product.sku);
        return { sku: product.sku, quantity: existingField?.quantity ?? 1 };
      });
      replace(newComponents);
      setSelectedProductsMap(new Map(newValue.map((product) => [product.sku, product])));
    },
    [fields, replace],
  );

  const onChangeQuantity = useCallback(
    (index: number, quantity: string) => {
      const parsedQuantity = quantity === '' ? undefined : parseInt(quantity, 10);
      setValue(
        `components.${index}.quantity`,
        (Number.isFinite(parsedQuantity) ? parsedQuantity : undefined) as number,
        { shouldValidate: true },
      );
    },
    [setValue],
  );

  const isStepDisabled = useCallback(
    (step: number) => {
      switch (step) {
        case 1:
          return Boolean(
            errors.name ||
            errors.sku ||
            errors.price ||
            !watch('name') ||
            !watch('sku') ||
            watch('price') === undefined,
          );
        case 2:
          return fields.length < 2;
        case 3: {
          const liveComponents = watch('components') ?? [];
          return (
            Boolean(errors.components) || liveComponents.some((component) => !component?.quantity)
          );
        }
        default:
          return false;
      }
    },
    [errors, fields, watch],
  );

  const onSubmit = async (data: CreateKitFormData) => {
    setIsCreating(true);
    try {
      await http.inventory.createKit({
        sku: data.sku,
        name: data.name,
        value: data.price,
        components: data.components,
      });
      toast.success('Kit creado correctamente.');
      await onProductsAdded?.();
      onClose();
    } catch {
      toast.error('Hubo un error al crear el Kit.');
    } finally {
      setIsCreating(false);
    }
  };

  return {
    currentStep,
    handleChangeStep,
    register,
    errors,
    watch,
    handleSubmit,
    onSubmit,
    isCreating,
    catalogSearch,
    catalogProducts,
    isLoadingCatalog,
    onOpenCatalog,
    onChangeCatalogInput,
    selectedProducts,
    onChangeSelectedProducts,
    onChangeQuantity,
    fields,
    selectedProductsMap,
    isStepDisabled,
  };
};

export default useCreateKit;
